import React, { useState, useEffect, useMemo } from "react";
import {
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  IconButton,
  useTheme,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  InfoOutlined as InfoIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import * as Styled from "./styles";
import {
  useValidation,
  useProviderFormContext,
} from "../../Context/ValidationContext";
import { getProviderCapabilities } from "../../utils/providerHelpers";

interface BrandFormData {
  brand: string;
}

const CreditCardBrands: React.FC = () => {
  const {
    selectedProvider,
    setValue,
    watch,
    formErrors: errors,
    internalReadOnly: readOnly,
    providers,
  } = useProviderFormContext();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [newBrandForm, setNewBrandForm] = useState<BrandFormData>({
    brand: "",
  });
  const [localErrors, setLocalErrors] = useState<{
    brand?: string;
  }>({});
  const theme = useTheme();
  const { setTabErrors, isValidationActive } = useValidation();

  const watchedProviderId = watch("provider");

  const currentProvider = useMemo(() => {
    if (selectedProvider) return selectedProvider;
    if (watchedProviderId && providers.length > 0) {
      return providers.find((p) => p.id === watchedProviderId) || null;
    }
    return null;
  }, [selectedProvider, watchedProviderId, providers]);

  const capabilities = useMemo(
    () => getProviderCapabilities(currentProvider),
    [currentProvider],
  );

  const watchedBrands = watch("brands");
  const brands = useMemo(() => watchedBrands || [], [watchedBrands]);

  const brandsError = errors.brands;
  const hasBrandsFormError = !!brandsError;
  const hasBrandsValidationError = isValidationActive && brands.length === 0;

  const brandOptions = useMemo(() => {
    if (!currentProvider?.schema?.properties?.acquirer?.properties?.brands) {
      return [];
    }

    const brandsSchema =
      currentProvider.schema.properties.acquirer.properties.brands;

    let allBrands: string[] = [];

    if (brandsSchema.items?.enum) {
      allBrands = brandsSchema.items.enum;
    } else if (Array.isArray(brandsSchema.enum)) {
      allBrands = brandsSchema.enum;
    }

    const availableBrands = allBrands.filter(
      (brand: string) => !brands.includes(brand),
    );

    return availableBrands.map((brand: string) => ({
      name: brand.charAt(0).toUpperCase() + brand.slice(1),
      value: brand,
    }));
  }, [currentProvider, brands]);

  const brandOptionsMap = useMemo(() => {
    return new Map<string, string>(
      brandOptions.map((option: { value: string; name: string }) => [
        option.value,
        option.name,
      ]),
    );
  }, [brandOptions]);

  useEffect(() => {
    if (isValidationActive && capabilities.brandsRequired) {
      const hasErrors = brands.length === 0;
      setTabErrors("creditCardBrands", hasErrors);
    } else if (!capabilities.hasBrands) {
      setTabErrors("creditCardBrands", false);
    }
  }, [
    brands,
    isValidationActive,
    setTabErrors,
    capabilities.brandsRequired,
    capabilities.hasBrands,
  ]);

  const handleAddBrand = () => {
    const newErrors: { brand?: string } = {};

    if (!newBrandForm.brand) {
      newErrors.brand = "Selecione uma bandeira";
    }

    if (Object.keys(newErrors).length > 0) {
      setLocalErrors(newErrors);
      return;
    }

    setLocalErrors({});

    const newBrand = newBrandForm.brand;

    const updatedBrands = [...brands, newBrand];
    setValue("brands", updatedBrands);
    setNewBrandForm({ brand: "" });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (brandIndex: number) => {
    const updatedBrands = brands.filter((_, index) => index !== brandIndex);
    setValue("brands", updatedBrands);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - brands.length) : 0;

  return (
    <Styled.Container>
      <Styled.ImportantNotice>
        <InfoIcon sx={{ color: theme.palette.text.primary }} />
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          <Styled.ImportantLabel>Importante: </Styled.ImportantLabel>
          Caso não encontre os dados necessários para preenchimento das
          informações no portal do Provedor, entre em contato com o suporte
          desse Provedor.
        </Typography>
      </Styled.ImportantNotice>

      <Styled.FormContainer>
        {brandOptions.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            Selecione um provedor para exibir as bandeiras disponíveis.
          </Typography>
        ) : (
          <Styled.FormRow>
            <TextField
              required
              name="brand"
              label="Bandeira"
              value={newBrandForm.brand}
              onChange={(e) => {
                setNewBrandForm((prev) => ({ ...prev, brand: e.target.value }));
                if (localErrors.brand) {
                  setLocalErrors((prev) => ({ ...prev, brand: undefined }));
                }
              }}
              select
              fullWidth
              size="small"
              variant="outlined"
              error={
                !!localErrors.brand ||
                hasBrandsFormError ||
                hasBrandsValidationError
              }
              helperText={localErrors.brand}
              disabled={readOnly}
            >
              {brandOptions.map((option: { value: string; name: string }) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <Styled.AddButton
              type="button"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddBrand}
              disabled={readOnly}
            >
              ADICIONAR BANDEIRA
            </Styled.AddButton>
          </Styled.FormRow>
        )}
      </Styled.FormContainer>

      <Styled.TableContainer
        sx={
          isValidationActive && brands.length === 0
            ? { border: "2px solid", borderColor: "error.main" }
            : undefined
        }
      >
        <Styled.Table>
          <TableHead>
            <TableRow>
              <Styled.TableCell>Bandeira</Styled.TableCell>
              <Styled.TableCell align="center">Ações</Styled.TableCell>
            </TableRow>
          </TableHead>
          <Styled.TableBody>
            {brands.length === 0 ? (
              <TableRow>
                <Styled.EmptyTableCell colSpan={2} align="center">
                  <Styled.EmptyStateTypography variant="body2">
                    Você não possui bandeiras cadastradas
                  </Styled.EmptyStateTypography>
                </Styled.EmptyTableCell>
              </TableRow>
            ) : (
              brands
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((brand: string, index: number) => {
                  const globalIndex = page * rowsPerPage + index;
                  const displayName = brandOptionsMap.get(brand) ?? brand;
                  return (
                    <TableRow key={`${brand}-${globalIndex}`}>
                      <Styled.TableCell>{displayName}</Styled.TableCell>
                      <Styled.TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(globalIndex)}
                          color="error"
                          disabled={readOnly}
                        >
                          <DeleteIcon fontSize="medium" />
                        </IconButton>
                      </Styled.TableCell>
                    </TableRow>
                  );
                })
            )}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <Styled.TableCell colSpan={2}> </Styled.TableCell>
              </TableRow>
            )}
          </Styled.TableBody>
        </Styled.Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={brands.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Styled.TableContainer>
    </Styled.Container>
  );
};

export default CreditCardBrands;
